import { Box, Typography } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { NULLISH_CELL_VALUE } from '../../DataGridCustom.types';
import { RouteId } from '~/routes';

import { LinkStyled, Root, UserAvatarStyled } from './UserCell.styled';

export type UserCellValue = Union.Nullable<{
  id: string;
  firstName?: Union.Nullable<string>;
  lastName?: Union.Nullable<string>;
  email?: Union.Nullable<string>;
  pictureId?: Union.Nullable<string>;
  workingHours?: Union.Nullable<number>;
}>;

interface UserCellProps<R extends GridValidRowModel = any> {
  value: UserCellValue;
  row: R;
  i18nKeyPrefix?: string;
  i18ns?: string;
  hideAvatar?: boolean;
}

export const UserCell = <R extends GridValidRowModel = any>({
  value,
  i18ns,
  i18nKeyPrefix,
  hideAvatar,
}: UserCellProps<R>) => {
  const { t } = useTranslation(i18ns, { keyPrefix: i18nKeyPrefix });
  const fullName = useMemo(
    () => [value?.firstName, value?.lastName].filter(Boolean).join(' '),
    [value?.firstName, value?.lastName],
  );
  if (!value) {
    return <>{NULLISH_CELL_VALUE}</>;
  }

  let result = (
    <Root>
      {hideAvatar ? null : (
        <Box
          sx={{
            mr: 3,
          }}
        >
          <UserAvatarStyled alt={fullName} user={value} />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: 'text.primary',
          }}
        >
          {fullName}
        </Typography>
        {value.workingHours ? (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
            }}
          >
            {t('workingHours', { count: value.workingHours })}
          </Typography>
        ) : null}
      </Box>
    </Root>
  );
  if (value.id) {
    result = <LinkStyled to={RouteId.userProfile(value.id)}>{result}</LinkStyled>;
  }
  return result;
};
